import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface UseFileUploadProps {
  allowedFileTypes: string[];
  onFileSelect?: (file: File | null) => void;
}

interface UseFileUploadReturn {
  file: File | null;
  error: string | null;
  isDragging: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragEnter: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveFile: () => void;
}

export const useFileUpload = ({
  allowedFileTypes,
  onFileSelect,
}: UseFileUploadProps): UseFileUploadReturn => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    dragCounter.current = 0;
    setError(null);
    const file = e.dataTransfer?.files[0];
    if (file && allowedFileTypes.includes(file.type)) {
      setFile(file);
      onFileSelect?.(file);
    } else {
      setError(t('file-upload.invalid-file-type-pdf'));
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    if (file && allowedFileTypes.includes(file.type)) {
      setFile(file);
      onFileSelect?.(file);
    } else {
      setError(t('file-upload.invalid-file-type-pdf'));
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setError(null);
    onFileSelect?.(null);
  };

  return {
    file,
    error,
    isDragging,
    fileInputRef,
    handleDrop,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleFileChange,
    handleRemoveFile,
  };
};
